import React from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Per-route error boundary. Catches uncaught render errors in a page and shows
 * a friendly card with a Retry button instead of letting the page go blank.
 * Resets automatically when the URL changes.
 */
class ErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }
  static getDerivedStateFromError(error) { return { error } }
  componentDidCatch(error, info) {
    // Surface full stack in devtools so we don't have to guess from a one-line message.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info?.componentStack)
  }
  componentDidUpdate(prev) {
    if (prev.routeKey !== this.props.routeKey && this.state.error) {
      this.setState({ error: null })
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div className="p-6 md:p-10 max-w-xl mx-auto">
          <div className="bg-white rounded-2xl ring-1 ring-rose-200/80 shadow-sm p-5">
            <div className="text-[11px] uppercase tracking-wider font-medium text-rose-600">Something went wrong</div>
            <h1 className="mt-1 text-[18px] font-semibold text-zinc-900">This page failed to render</h1>
            <pre className="mt-3 text-[12px] text-zinc-600 bg-zinc-50 p-3 rounded-lg overflow-auto max-h-48 ring-1 ring-zinc-200/60 whitespace-pre-wrap">
              {String(this.state.error?.message || this.state.error)}
            </pre>
            <button
              onClick={() => this.setState({ error: null })}
              className="mt-4 px-3 py-1.5 rounded-lg text-[13px] font-semibold bg-amber-500 text-white hover:bg-amber-600 shadow-sm shadow-amber-500/20"
            >
              Retry
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

export default function ErrorBoundary({ children }) {
  const location = useLocation()
  return <ErrorBoundaryInner routeKey={location.pathname}>{children}</ErrorBoundaryInner>
}
